import {
  DataTableProviderFilterItem,
  DataTableProviderFilterStateValue,
  DataTableProviderProps
} from './types';
import Card, { CardBody } from '../../ui/Card';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import DataTable from '../../ui/DataTable';
import SelectField from '../../ui/SelectField';
import {
  Button,
  ArrowLeftRightIcon,
  InlineNotification,
  InlineNotificationModes,
  SearchInput,
  NavigationActionBar,
  Dropdown,
  useOnClickOutside,
  EllipsisVerticalIcon,
  IconButtonModes,
  IconButtonVariants,
  ButtonSizes,
  Drawer,
  DrawerStage,
  XIcon,
  RotateCcwIcon,
  XCircleIcon,
  FilterIcon,
  IconButton,
  useDrawerContext
} from 'stubben-edge-unified-frontend-react-components';
import { DataTableSortData } from '@ui/DataTable/types';
import IconFileReview from '@icons/IconFileReview';
import { useLocation } from 'react-router-dom';
import { resolveInitialSortFromSession } from './resolveInitialSortFromSession';
import { buildAppliedSortTags } from './buildAppliedSortTags';
import { AppliedSortTags } from './AppliedSortTags';
import { useQuery } from '@tanstack/react-query';
import { renderFilterDrawerGrid } from './datatableFilterDrawerFields';
import { appliedFiltersFingerprint } from './appliedFiltersFingerprint';
import {
  parseFilterQueryParams,
  writeDatatableFiltersToUrl
} from './syncFiltersToSearchParams';
import {
  clampRequestedPage,
  lastPageFromPagination,
  resolvePersistedPagination,
  writeDatatablePaginationToUrl
} from './persistDatatablePagination';
import {
  useDatatableFilterDraftHandlers,
  postFilterChangeDependants
} from './useDatatableFilterDraftHandlers';
import { withPipelineContextFilter } from '@/components/common/TasksListComponent/pipelineContextFilters';
import SavedFiltersDrawerSection from './SavedFiltersDrawerSection';
import type { SavedFiltersDrawerSectionHandle } from './SavedFiltersDrawerSection';
import { DATATABLE_FILTER_DRAWER } from '@/constants/drawers';

type FetchOrigin = 'user' | 'poll';

// Session storage utilities
interface SerializedFilterState {
  filters: Array<[string, DataTableProviderFilterStateValue]>;
  searchTerm: string;
  sort: DataTableSortData | null;
  page?: number;
  perPage?: number;
}

/**
 * Generates a unique storage key for the datatable state.
 * Uses tableId if provided, otherwise falls back to pathname + keyField + title hash.
 */
const getStorageKey = (
  pathname: string,
  keyField: string,
  tableId?: string,
  title?: string
): string => {
  const pathnamePart = pathname.replaceAll('/', '_');

  if (tableId) {
    // Use explicit tableId for maximum clarity
    return `datatable_state${pathnamePart}_${tableId}`;
  }

  // Generate a unique suffix from available props
  const titleHash = title ? `_${title.toLowerCase().replace(/\s+/g, '-')}` : '';
  return `datatable_state${pathnamePart}_${keyField}${titleHash}`;
};

const saveToSessionStorage = (
  key: string,
  filters: Map<string, DataTableProviderFilterStateValue>,
  searchTerm: string,
  sort: DataTableSortData | null | undefined,
  pagination?: { page: number; perPage: number }
): void => {
  try {
    const data: SerializedFilterState = {
      filters: Array.from(filters.entries()),
      searchTerm,
      sort: sort || null,
      ...(pagination
        ? { page: pagination.page, perPage: pagination.perPage }
        : {})
    };
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save datatable state to session storage:', error);
  }
};

const loadFromSessionStorage = (key: string): SerializedFilterState | null => {
  try {
    const data = sessionStorage.getItem(key);
    if (!data) return null;
    const parsed = JSON.parse(data) as SerializedFilterState;
    return parsed;
  } catch (error) {
    console.warn('Failed to load datatable state from session storage:', error);
    return null;
  }
};

const clearSessionStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(
      'Failed to clear datatable state from session storage:',
      error
    );
  }
};

/** Mirrors datatable persistence `useEffect`; call synchronously on apply/remove so unmount beats passive effects. */
function persistDatatableSessionSnapshot(
  key: string,
  filters: Map<string, DataTableProviderFilterStateValue>,
  searchTerm: string,
  sort: DataTableSortData | null | undefined,
  pagination?: { page: number; perPage: number }
): void {
  const existing = pagination ? null : loadFromSessionStorage(key);
  const nextPagination =
    pagination ??
    (existing?.page != null || existing?.perPage != null
      ? {
          page: existing?.page ?? 1,
          perPage: existing?.perPage ?? 10
        }
      : undefined);
  const hasPagination =
    nextPagination != null &&
    (nextPagination.page !== 1 || nextPagination.perPage !== 10);

  if (filters.size > 0 || searchTerm !== '' || sort || hasPagination) {
    saveToSessionStorage(key, filters, searchTerm, sort, nextPagination);
  } else {
    clearSessionStorage(key);
  }
}

export default function DataTableProvider<T>(props: DataTableProviderProps<T>) {
  const {
    columns,
    column2,
    fetchResolver,
    onFetchComplete,
    onDataLoadingChange,
    onActiveFiltersChange,
    syncFiltersToUrl = false,
    restoreSessionSortWhenUrlHasParams = false,
    showAppliedSortInFilterDrawer = false,
    paginationProps,
    actions,
    bulkActions,
    bulkActionsAvailability,
    extraActions,
    navigationActionBarClassName,
    navigationActionBarFilterButtonTestIdentifier,
    navigationActionBarSearchTestIdentifier,
    navigationActionBarBulkDropdownTestIdentifier,
    keyField = 'id',
    uniqueKeyField = '',
    searchable,
    composable,
    searchPlaceholder,
    filtersList,
    filtersTitleTestId,
    filterDrawerTitle,
    showDrawerResetFilters,
    selectAllHeaderTestId,
    actionsHeaderTestId,
    rowSelectTestIdentifier,
    onRowClicked,
    onCellClick,
    defaultSort,
    defaultFilter,
    contextPipeline,
    sanitizeSessionFilters,
    savedFilterSection,
    savedFilterContextId,
    emptyState,
    outdatedTimestamp,
    isSeparated,
    noResultsText,
    appendAfterRow,
    isResponsive,
    showPagination,
    showBulkError,
    bulkErrorText,
    handleBulkErrorClick,
    itemsToArchiveCount,
    isFixedLayout,
    showTitle,
    title = '',
    subtitle = '',
    refreshTime,
    useNewActionbar = false,
    navigationActionBarTitle,
    filled = false,
    gcTime = 1000 * 60 * 30, // 30 minutes
    tableId, // tableId is the unique identifier for the data table and is used to store the state in session storage. It should be unique for each data table and should be snake_case.
    persistPagination = false
  } = props;

  const location = useLocation();

  // Generate unique storage key for this table
  const storageKey = useMemo(
    () => getStorageKey(location.pathname, keyField as string, tableId, title),
    [location.pathname, keyField, tableId, title]
  );

  /** Stable prefix for E2E: `datatable-tasks-search`, `datatable-policies-bulk-apply`, etc. */
  const dataTableTestPrefix = useMemo(() => {
    if (tableId) {
      return `datatable-${String(tableId).replace(/_/g, '-')}`;
    }
    const routeSegment =
      location.pathname
        .replace(/^\//, '')
        .replace(/\//g, '-')
        .replace(/_/g, '-') || 'root';
    return `datatable-${routeSegment}`;
  }, [location.pathname, tableId]);

  const getFilterTestIdentifier = useCallback(
    (filterKey: string) =>
      `${dataTableTestPrefix}-filter-${filterKey.replace(/[_.]/g, '-')}`,
    [dataTableTestPrefix]
  );

  const resolveRowSelectTestIdentifier = useCallback(
    (row: T) =>
      rowSelectTestIdentifier
        ? rowSelectTestIdentifier(row)
        : `${dataTableTestPrefix}-row-select-${String(row[keyField as keyof T])}`,
    [rowSelectTestIdentifier, dataTableTestPrefix, keyField]
  );

  const resolvedSelectAllHeaderTestId =
    selectAllHeaderTestId ?? `${dataTableTestPrefix}-select-all`;

  const resolvedBulkErrorDismissTestId = `${dataTableTestPrefix}-bulk-error-dismiss`;

  const resolvedActionsHeaderTestId = actions
    ? (actionsHeaderTestId ?? `${dataTableTestPrefix}-actions-header`)
    : undefined;

  const sessionSnapshot = useMemo(
    () => loadFromSessionStorage(storageKey),
    [storageKey]
  );

  const initialFilters = useMemo(() => {
    const merged = new Map(defaultFilter ?? []);
    sessionSnapshot?.filters?.forEach(([key, filterState]) => {
      merged.set(key, filterState);
    });
    return merged;
  }, [defaultFilter, sessionSnapshot]);

  const initialSort = useMemo(
    () =>
      resolveInitialSortFromSession({
        session: sessionSnapshot,
        search: window.location.search,
        defaultFilter,
        defaultSort,
        restoreSessionSortWhenUrlHasParams:
          restoreSessionSortWhenUrlHasParams || persistPagination
      }),
    [
      sessionSnapshot,
      defaultFilter,
      defaultSort,
      restoreSessionSortWhenUrlHasParams,
      persistPagination
    ]
  );

  // Data state
  // const [items, setItems] = useState<Array<T>>([]);
  const {
    data,
    refetch,
    isLoading: loading,
    isFetching
  } = useQuery({
    queryKey: ['datatable', location.pathname, keyField, tableId || title],
    queryFn: () => fetchItems(),
    enabled: false,
    gcTime: gcTime,
    // fetchItems rethrows; a retry would re-run it under a new request id.
    retry: false,
    // BUGS-3021: the default 'online' mode pauses the fetch while the browser
    // is offline, so it never fails and the stale rows stay. Always run it so
    // the user sees the error.
    networkMode: 'always'
  });
  // BUGS-3021: set only by the latest request's failure, cleared by its success.
  const [loadError, setLoadError] = useState<unknown>(null);
  // BUGS-3020: who started the in-flight request. Polls must not show the
  // refresh overlay (the PR #723 export-history flicker).
  const [activeOrigin, setActiveOrigin] = useState<FetchOrigin | null>(null);
  const nextOriginRef = useRef<FetchOrigin>('user');
  const hasDataRef = useRef(false);
  hasDataRef.current = data !== undefined;
  const showError = loadError !== null && !loading;
  const isRefreshing =
    activeOrigin === 'user' && isFetching && data !== undefined;
  // Stale rows stay in the query cache on failure; never show or select them.
  const items = showError ? [] : data?.items || [];
  const isDataBusy = loading || isFetching;

  useEffect(() => {
    onDataLoadingChange?.(isDataBusy);
    return () => {
      onDataLoadingChange?.(false);
    };
  }, [isDataBusy, onDataLoadingChange]);

  // Pagination state
  const persistedPagination = useMemo(
    () =>
      resolvePersistedPagination({
        persistPagination,
        search: window.location.search,
        snapshot: persistPagination ? sessionSnapshot : null
      }),
    [persistPagination, storageKey]
  );
  const [page, setPage] = useState(persistedPagination.page);
  const [perPage, setPerPage] = useState(persistedPagination.perPage);
  const [total, setTotal] = useState(0);
  const pageRef = useRef(page);
  const perPageRef = useRef(perPage);
  const isActiveRef = useRef(true);
  const searchTimerRef = useRef<number | undefined>(undefined);
  // BUGS-3021: bumped per request (and on unmount) so only the latest
  // response may touch table state.
  const latestRequestIdRef = useRef(0);
  const tablePathnameRef = useRef(location.pathname);
  pageRef.current = page;
  perPageRef.current = perPage;
  tablePathnameRef.current = location.pathname;

  const canWriteHistory = useCallback(
    () =>
      isActiveRef.current &&
      tablePathnameRef.current === window.location.pathname,
    []
  );

  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
      latestRequestIdRef.current += 1;
      if (searchTimerRef.current !== undefined) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = undefined;
      }
    };
  }, []);

  // Bulk state
  const [selected, setSelected] = useState<Array<T>>([]);
  useEffect(() => {
    setSelected([]);
  }, [showBulkError]);
  const [selectedBulkAction, setSelectedBulkAction] = useState<string | null>(
    null
  );
  const [bulkLoading, setBulkLoading] = useState(false);
  const disabledBulkIds = bulkActionsAvailability
    ? items
        .filter((i) => !bulkActionsAvailability(i))
        .map((i) => i[keyField as keyof T])
    : [];

  const urlSearchTerm = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || params.get('q') || '';
  })();
  const [searchTerm, setSearchTerm] = useState(
    urlSearchTerm || sessionSnapshot?.searchTerm || ''
  );
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  const filterKeys = useMemo(
    () => filtersList?.map((item) => item.filterKey) ?? [],
    [filtersList]
  );

  const writeFiltersToUrl = useCallback(
    (
      filters: Map<string, DataTableProviderFilterStateValue>,
      nextSearchTerm = searchTermRef.current
    ) => {
      if (!canWriteHistory()) return;
      if (syncFiltersToUrl) {
        writeDatatableFiltersToUrl({
          filters,
          searchTerm: nextSearchTerm,
          filterKeys,
          pathname: location.pathname
        });
      }
      if (persistPagination) {
        writeDatatablePaginationToUrl({
          page: pageRef.current,
          perPage: perPageRef.current,
          pathname: location.pathname
        });
      }
    },
    [
      canWriteHistory,
      syncFiltersToUrl,
      persistPagination,
      filterKeys,
      location.pathname
    ]
  );

  useEffect(() => {
    if (!persistPagination || !canWriteHistory()) return;
    writeDatatablePaginationToUrl({
      page: pageRef.current,
      perPage: perPageRef.current,
      pathname: location.pathname
    });
  }, [canWriteHistory, persistPagination, location.pathname]);

  // Sorting state - initialize from session (opt-in restores even with URL params)
  const [sort, setSort] = useState<DataTableSortData | null | undefined>(
    initialSort
  );

  // Filters drawer via DS context — unique name per table instance
  const drawerCtx = useDrawerContext();
  const filterDrawerName = `${DATATABLE_FILTER_DRAWER}-${dataTableTestPrefix}`;
  const savedFiltersRef = useRef<SavedFiltersDrawerSectionHandle | null>(null);

  // Mobile dropdown state
  const dropdownWrapperRef = useRef<HTMLDivElement | null>(null);
  const dropdownButtonRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDropdownClickOutside = useCallback(
    () => setDropdownOpen(false),
    []
  );

  useOnClickOutside<HTMLDivElement>(
    [dropdownWrapperRef, dropdownButtonRef, dropdownRef],
    handleDropdownClickOutside
  );
  const [filtersState, setFiltersState] =
    useState<Map<string, DataTableProviderFilterStateValue>>(
      () => new Map(initialFilters)
    );
  const [activeFilters, setActiveFilters] =
    useState<Map<string, DataTableProviderFilterStateValue>>(
      () => new Map(initialFilters)
    );

  const [newFilters, setNewFilters] =
    useState<Map<string, DataTableProviderFilterStateValue>>(
      () => new Map(initialFilters)
    );

  const {
    handleFilterInputChange,
    handleFilterSelectChange,
    handleAutoCompleteChange
  } = useDatatableFilterDraftHandlers(filtersState, setFiltersState);

  const [newFetchCompletionTimestamp, setNewFetchCompletionTimestamp] =
    useState<string>();

  // Sort drawer state
  const [sortOpen, setSortOpen] = useState(false);

  // Save state whenever filters/search/sort change (also persists synchronously from apply/remove/reset handlers)
  useEffect(() => {
    persistDatatableSessionSnapshot(
      storageKey,
      activeFilters,
      searchTerm,
      sort,
      persistPagination ? { page, perPage } : undefined
    );
  }, [activeFilters, searchTerm, sort, storageKey, persistPagination, page, perPage]);

  useEffect(() => {
    onActiveFiltersChange?.(activeFilters);
  }, [activeFilters, onActiveFiltersChange]);

  // Overlay URL filter values onto the restored snapshot. Defaults and
  // session values stay for keys the URL does not initialise.
  useEffect(() => {
    if (!filtersList || filtersList.length === 0) return;

    const searchParams = new URLSearchParams(window.location.search);

    // Async function to fetch filter labels
    const initializeFilters = async () => {
      const urlFilters = new Map<string, DataTableProviderFilterStateValue>();

      // Process filters sequentially to handle dependencies (e.g., pipeline_stage depends on pipeline)
      for (const filterItem of filtersList) {
        const urlValue = searchParams.get(filterItem.filterKey);
        if (urlValue) {
          // Handle date_range and other array-based filters (comma-separated values)
          let value: string | string[] | number = urlValue;
          let label: string | string[] | number = urlValue;

          if (filterItem.type === 'date_range') {
            value = urlValue.split(',');
            label = value;
          } else if (filterItem.type === 'number') {
            value = Number(urlValue);
            label = urlValue;
          } else if (filterItem.type === 'autocomplete') {
            // For autocomplete filters, try to fetch the label using itemResolver
            try {
              const autoCompleteOptions =
                filterItem.autoCompleteOptions?.(urlFilters);
              if (autoCompleteOptions?.itemResolver) {
                const item = await autoCompleteOptions.itemResolver(urlValue);
                if (item) {
                  label = item.label;
                }
              }
            } catch (error) {
              console.warn(
                `Failed to fetch label for ${filterItem.filterKey}:`,
                error
              );
              // Keep the value as label if fetch fails
            }
          } else if (
            filterItem.type === 'select' ||
            filterItem.type === 'choice_chips'
          ) {
            // Try to find a label from select options
            if (filterItem.selectOptions) {
              const option = filterItem.selectOptions.find(
                (opt) => String(opt.value) === urlValue
              );
              if (option) {
                label = String(option.label);
              }
            } else if (filterItem.selectOptionsResolver) {
              // Fetch options asynchronously if resolver is provided
              try {
                const options = await filterItem.selectOptionsResolver();
                const option = options?.find(
                  (opt) => String(opt.value) === urlValue
                );
                if (option) {
                  label = String(option.label);
                }
              } catch (error) {
                console.warn(
                  `Failed to fetch select options for ${filterItem.filterKey}:`,
                  error
                );
              }
            }
          } else {
            // For other types, keep the value as label
            label = urlValue;
          }

          urlFilters.set(filterItem.filterKey, {
            value: value,
            label: label,
            item: filterItem
          });
        }
      }

      if (urlFilters.size > 0) {
        const mergeUrl = (
          prev: Map<string, DataTableProviderFilterStateValue>
        ) => {
          const next = new Map(prev);
          urlFilters.forEach((filterState, key) => {
            next.set(key, filterState);
          });
          return next;
        };
        setFiltersState(mergeUrl);
        setActiveFilters(mergeUrl);
        setNewFilters(mergeUrl);
      }
    };

    // Check for search term in URL (common parameter names)
    const searchValue = searchParams.get('search') || searchParams.get('q');
    if (searchValue) {
      setSearchTerm(searchValue);
    }

    // Execute async initialization
    initializeFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const fetchItems = async () => {
    // Read synchronously: refetch() runs this queryFn before it returns.
    const origin = nextOriginRef.current;
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    const isLatest = () =>
      isActiveRef.current && requestId === latestRequestIdRef.current;
    setActiveOrigin(origin);

    const filtersObj = parseFilterQueryParams(window.location.search);

    if (filtersList && newFilters?.size) {
      newFilters.forEach((state, key) => {
        filtersObj[key] = state.value;
      });
    }

    const request = (requestedPage: number) =>
      fetchResolver({
        page: requestedPage,
        perPage,
        searchTerm: searchTerm && searchTerm !== '' ? searchTerm : undefined,
        sort: sort ?? undefined,
        filters: filtersObj
      });

    try {
      let data = await request(page);
      // A newer request owns the table; TanStack already dropped this result.
      if (!isLatest()) return data;

      if (persistPagination) {
        const lastPage = lastPageFromPagination({
          page: Number(data.pagination.page),
          perPage: Number(data.pagination.perPage),
          total: Number(data.pagination.total),
          lastPage: data.pagination.lastPage
        });
        const clamped = clampRequestedPage(
          page,
          lastPage,
          Number(data.pagination.total)
        );
        if (clamped !== page) {
          setPage(clamped);
          pageRef.current = clamped;
          if (canWriteHistory()) {
            writeDatatablePaginationToUrl({
              page: clamped,
              perPage: perPageRef.current,
              pathname: location.pathname
            });
          }
          // Same request id: the clamp is part of this fetch, not a new one.
          data = await request(clamped);
          if (!isLatest()) return data;
        }
      }

      // setItems(data.items);
      setSelected([]);
      setSelectedBulkAction(null);
      const nextPage = Number(data.pagination.page);
      const nextPerPage = Number(data.pagination.perPage);
      const nextTotal = Number(data.pagination.total);
      if (nextPage !== Number(pageRef.current)) setPage(nextPage);
      if (nextPerPage !== Number(perPage)) setPerPage(nextPerPage);
      if (nextTotal !== Number(total)) setTotal(nextTotal);
      setLoadError(null);

      onFetchComplete?.(data);
      return data;
    } catch (e) {
      console.error('Datatable Error:', e);
      // A failed background poll keeps the rows; the next poll retries.
      if (isLatest() && !(origin === 'poll' && hasDataRef.current)) {
        setSelected([]);
        setLoadError(e ?? new Error('Request failed'));
      }
      throw e;
    } finally {
      if (isLatest()) {
        setActiveOrigin(null);
        setNewFetchCompletionTimestamp(new Date().toISOString());
      }
    }
  };

  useEffect(() => {
    if (refreshTime && newFetchCompletionTimestamp && !loading) {
      const interval = setTimeout(() => {
        nextOriginRef.current = 'poll';
        try {
          // Join an in-flight user request rather than cancelling it.
          void refetch({ cancelRefetch: false });
        } finally {
          nextOriginRef.current = 'user';
        }
      }, refreshTime);
      return () => clearTimeout(interval);
    }
    // `loading` too: the completion timestamp can land before TanStack clears
    // isLoading, and the first poll must still be scheduled.
  }, [newFetchCompletionTimestamp, loading]);

  useEffect(() => {
    refetch();
  }, [newFilters, outdatedTimestamp, refreshTime, sort]);

  const bulkActionOptions = bulkActions?.map((b, index) => ({
    value: b.label,
    icon: b.icon,
    label: b.label
  }));

  const handleRetry = () => {
    void refetch();
  };

  const handleBulkActionTrigger = async () => {
    if (!selected || selected.length === 0) {
      alert('No items selected');
      return;
    }
    const theBulkAction = bulkActions?.find(
      (b) => b.label === selectedBulkAction
    );
    if (!theBulkAction) return;
    setBulkLoading(true);
    const status = await theBulkAction.action(selected);
    if (status) {
      // fetchItems().then();
      refetch();
      setSelected([]);
    }
    setBulkLoading(false);
  };

  const handleApplyFilter = (
    resetPage = true,
    options?: { force?: boolean }
  ) => {
    if (!isActiveRef.current) return;
    const nextFilters = new Map<string, DataTableProviderFilterStateValue>();
    filtersState?.forEach((state, key, i) => {
      if (state && state.value && state.value !== '')
        nextFilters.set(key, state);
    });
    const applied = sanitizeSessionFilters
      ? sanitizeSessionFilters(nextFilters)
      : nextFilters;

    if (
      persistPagination &&
      resetPage &&
      !options?.force &&
      appliedFiltersFingerprint(applied) ===
        appliedFiltersFingerprint(newFilters)
    ) {
      drawerCtx.close(filterDrawerName);
      return;
    }

    if (resetPage) {
      setPage(1);
      pageRef.current = 1;
    }
    setNewFilters(applied);
    setActiveFilters(applied);
    drawerCtx.close(filterDrawerName);
    persistDatatableSessionSnapshot(
      storageKey,
      applied,
      searchTermRef.current,
      sort,
      persistPagination
        ? { page: pageRef.current, perPage: perPageRef.current }
        : undefined
    );
    writeFiltersToUrl(applied);
  };

  // To minimise calls to api force a 2 seconds timeout after the user has stopped typing.
  const handleUpdateSearch = (searchValue: string) => {
    searchTermRef.current = searchValue;
    setSearchTerm(searchValue);
    if (searchTimerRef.current !== undefined) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = window.setTimeout(() => {
      searchTimerRef.current = undefined;
      handleApplyFilter(true, { force: true });
    }, 2000);
  };

  const handleSortChange = (sortedEntity: DataTableSortData) => {
    setSort(sortedEntity);
    setPage(1);
    pageRef.current = 1;
    persistDatatableSessionSnapshot(
      storageKey,
      activeFilters ?? new Map(),
      searchTerm,
      sortedEntity,
      persistPagination
        ? { page: 1, perPage: perPageRef.current }
        : undefined
    );
    writeFiltersToUrl(activeFilters ?? new Map());
  };

  const handleClearAppliedSort = () => {
    const nextSort = defaultSort ?? null;
    setSort(nextSort);
    setPage(1);
    pageRef.current = 1;
    persistDatatableSessionSnapshot(
      storageKey,
      activeFilters ?? new Map(),
      searchTerm,
      nextSort,
      persistPagination
        ? { page: 1, perPage: perPageRef.current }
        : undefined
    );
  };

  const appliedSortTags = useMemo(
    () =>
      showAppliedSortInFilterDrawer
        ? buildAppliedSortTags({
            sort,
            defaultSort,
            columns
          })
        : [],
    [showAppliedSortInFilterDrawer, sort, defaultSort, columns]
  );

  const handleChangePage = (nextPage: number) => {
    setPage(nextPage);
    pageRef.current = nextPage;
    handleApplyFilter(false);
  };

  const handleChangePerPage = (nextPerPage: number) => {
    setPerPage(nextPerPage);
    perPageRef.current = nextPerPage;
    handleApplyFilter(false);
  };

  const handleRemoveFilter = (filterKey: string) => {
    if (!activeFilters?.has(filterKey)) return;
    const newFilters = new Map(activeFilters);
    newFilters.delete(filterKey);

    if (filtersState?.has(filterKey)) {
      const newFiltersState = new Map(filtersState);
      const relevantState = newFiltersState.get(filterKey);
      if (relevantState?.value) {
        relevantState.value = '';
        newFiltersState.set(filterKey, relevantState);
        if (relevantState.item) {
          // Reset dependant filters
          postFilterChangeDependants(relevantState.item, newFiltersState);
          if (relevantState.item.dependants) {
            relevantState.item.dependants.forEach((key) => {
              newFilters.delete(key);
            });
          }
        }
        setFiltersState(newFiltersState);
      }
    }

    if (syncFiltersToUrl) {
      writeFiltersToUrl(newFilters);
    } else if (window.location.search !== '') {
      const searchParams = new URLSearchParams(location.search);
      if (filterKey === 'pipeline_id') {
        searchParams.delete(filterKey);
        searchParams.delete('pipeline_stage_id');
      } else {
        searchParams.delete(filterKey);
      }
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }

    setFiltersState(newFilters);
    setNewFilters(newFilters);
    setActiveFilters(newFilters);
    persistDatatableSessionSnapshot(storageKey, newFilters, searchTerm, sort);
  };

  const handleFilterReset = () => {
    const emptyFilters = new Map<string, DataTableProviderFilterStateValue>();
    searchTermRef.current = '';
    setPage(1);
    pageRef.current = 1;
    if (syncFiltersToUrl) {
      writeFiltersToUrl(emptyFilters, '');
    } else if (persistPagination) {
      const params = new URLSearchParams(window.location.search);
      filterKeys.forEach((key) => params.delete(key));
      params.delete('search');
      params.delete('q');
      params.set('page', '1');
      params.set('perPage', String(perPageRef.current));
      const query = params.toString();
      window.history.replaceState(
        {},
        document.title,
        query ? `${location.pathname}?${query}` : location.pathname
      );
    } else {
      window.history.replaceState({}, document.title, location.pathname);
    }

    setActiveFilters(emptyFilters);
    setNewFilters(emptyFilters);
    setFiltersState(emptyFilters);

    setSearchTerm('');
    setSort(null);

    persistDatatableSessionSnapshot(
      storageKey,
      emptyFilters,
      '',
      null,
      persistPagination ? { page: 1, perPage: perPageRef.current } : undefined
    );
  };

  const contextPipelineId = contextPipeline?.id;
  const contextPipelineName = contextPipeline?.name;

  useLayoutEffect(() => {
    if (!sanitizeSessionFilters) return;
    setActiveFilters((prev) => {
      const next = sanitizeSessionFilters(prev);
      if (appliedFiltersFingerprint(prev) === appliedFiltersFingerprint(next)) {
        return prev;
      }
      setFiltersState(next);
      setNewFilters(next);
      persistDatatableSessionSnapshot(storageKey, next, searchTerm, sort);
      return next;
    });
  }, [sanitizeSessionFilters, storageKey, searchTerm, sort]);

  const activeFiltersForUi = useMemo(() => {
    const m = new Map(activeFilters);
    m.delete('owner_uuid');
    if (
      contextPipelineId &&
      String(m.get('pipeline_id')?.value) === String(contextPipelineId)
    ) {
      m.delete('pipeline_id');
    }
    return m;
  }, [activeFilters, contextPipelineId]);

  const appliedFiltersSyncKey = useMemo(
    () => appliedFiltersFingerprint(activeFilters),
    [activeFilters]
  );

  const filtersDrawerOpen = drawerCtx.isOpen(filterDrawerName);

  useLayoutEffect(() => {
    if (filtersDrawerOpen) {
      setFiltersState(new Map(activeFilters));
    }
  }, [filtersDrawerOpen, activeFilters, appliedFiltersSyncKey]);

  useLayoutEffect(() => {
    if (!contextPipelineId) return;
    setActiveFilters((prev) => {
      if (prev.has('pipeline_id')) return prev;
      const next = withPipelineContextFilter(prev, {
        id: contextPipelineId,
        name: contextPipelineName ?? contextPipelineId
      });
      setFiltersState(next);
      setNewFilters(next);
      persistDatatableSessionSnapshot(storageKey, next, searchTerm, sort);
      return next;
    });
  }, [contextPipelineId, contextPipelineName, storageKey, searchTerm, sort]);

  const hasActiveFilters = activeFiltersForUi.size > 0;

  /** Default table sort is not “user-applied”; avoid showing Clear after reset when remounting (e.g. Board ↔ List). */
  const hasUserCustomSort = useMemo(() => {
    if (sort == null || sort === undefined) return false;
    if (defaultSort == null) return true;
    return (
      sort.field !== defaultSort.field ||
      sort.direction !== defaultSort.direction
    );
  }, [sort, defaultSort]);

  const isEmpty =
    emptyState &&
    !loading &&
    !loadError &&
    items.length === 0 &&
    !hasActiveFilters;
  const hasActions =
    bulkActionOptions || searchable || filtersList || composable;
  const hasFilters = filtersList && !isEmpty;

  // Checkbox handlers for NavigationActionBar
  const allSelected =
    !isEmpty && items.length > 0 && selected.length === items.length;
  const someSelected = selected.length > 0 && selected.length < items.length;

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Select all items
      setSelected(items);
    } else {
      // Deselect all
      setSelected([]);
    }
  };

  const handleBulkActionSelect = async (value: string | number) => {
    if (!selected || selected.length === 0) {
      alert('No items selected');
      return;
    }
    const theBulkAction = bulkActions?.find((b) => b.label === value);
    if (!theBulkAction) return;
    setBulkLoading(true);
    const status = await theBulkAction.action(selected);
    if (status) {
      // fetchItems().then();
      refetch();
      setSelected([]);
    }
    setBulkLoading(false);
  };

  const showClearFiltersForToolbar =
    hasActiveFilters ||
    (!!searchTerm && searchTerm !== '') ||
    hasUserCustomSort;

  const actionSectionToolbarContext = {
    clearFiltersAndSearch: handleFilterReset,
    showClearFilters: showClearFiltersForToolbar
  };

  const resolvedExtraActions: ReactNode =
    typeof extraActions === 'function'
      ? extraActions(actionSectionToolbarContext)
      : extraActions;

  return (
    <>
      {(hasActions || hasFilters || showTitle) && (
        <div>
          {hasActions && useNewActionbar && (
            <NavigationActionBar
              filled={filled}
              className={navigationActionBarClassName}
              pageTitle={navigationActionBarTitle}
              showBulkSelection={Boolean(bulkActions?.length)}
              searchPlaceholder={searchPlaceholder || 'Search'}
              searchValue={searchTerm}
              onSearchChange={handleUpdateSearch}
              searchInputTestIdentifier={
                navigationActionBarSearchTestIdentifier
              }
              checkboxChecked={allSelected}
              checkboxIndeterminate={someSelected}
              onCheckboxChange={handleCheckboxChange}
              dropdownOptions={bulkActionOptions}
              onDropdownOptionSelect={handleBulkActionSelect}
              bulkDropdownDisabled={selected.length === 0}
              bulkDropdownButtonTestIdentifier={
                navigationActionBarBulkDropdownTestIdentifier
              }
              filterActive={hasActiveFilters}
              filterCount={hasFilters ? activeFiltersForUi.size : undefined}
              onFilterClick={
                hasFilters ? () => drawerCtx.open(filterDrawerName) : undefined
              }
              filterButtonTestIdentifier={
                navigationActionBarFilterButtonTestIdentifier
              }
              actionSection={resolvedExtraActions}
              testIdentifier={`${dataTableTestPrefix}-action-bar`}
            />
          )}
          {hasActions && !useNewActionbar && (
            <Card
              raised={false}
              bgColor={isSeparated ? 'default' : 'dimmer'}
              padding="dense"
              className="mb-4"
            >
              <CardBody className="mx-6 flex flex-col items-center justify-between gap-2 space-x-4 md:flex-row md:gap-0">
                <div className="flex flex-col items-center space-x-4 md:flex-row ">
                  <span className="h5">Actions</span>
                  {bulkActionOptions && !isEmpty && (
                    <span className="flex flex-col flex-nowrap items-center justify-start space-x-4 md:flex-row">
                      <SelectField
                        disabled={!selected?.length}
                        placeholder="Bulk actions"
                        value={selectedBulkAction || ''}
                        onChange={(s) => setSelectedBulkAction(s)}
                        options={bulkActionOptions}
                        selectProps={
                          {
                            'data-testid': `${dataTableTestPrefix}-bulk-actions-select`
                          } as any
                        }
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={bulkLoading}
                        disabled={
                          !selected?.length || !selectedBulkAction || loading
                        }
                        onClick={handleBulkActionTrigger}
                        testIdentifier={`${dataTableTestPrefix}-bulk-apply`}
                      >
                        Apply
                      </Button>
                    </span>
                  )}
                </div>
                {searchable && !isEmpty && (
                  <div className="max-w-[221px]">
                    <SearchInput
                      placeholder={searchPlaceholder || 'Search'}
                      variant="secondary"
                      value={searchTerm}
                      size="sm"
                      onChange={(searchValue: string) =>
                        handleUpdateSearch(searchValue)
                      }
                      testIdentifier={`${dataTableTestPrefix}-search`}
                      inputProps={
                        {
                          'data-testid': `${dataTableTestPrefix}-search`
                        } as any
                      }
                    />
                  </div>
                )}
                {extraActions && (
                  <span className="flex flex-nowrap items-center justify-end space-x-4">
                    {resolvedExtraActions}
                  </span>
                )}
              </CardBody>
            </Card>
          )}
          {showTitle && (
            <Card
              padding="dense"
              raised={false}
              bgColor="default"
              className={`mt-4 duration-200 transition-opacity${
                loading ? ' pointer-events-none opacity-50' : ''
              }${hasFilters ? ' relative z-10 -mb-7' : ' mb-4'}`}
            >
              <CardBody>
                <div className="flex items-center py-2">
                  <IconFileReview className="mr-4 h-10 w-10 text-primary" />
                  <div>
                    <span className="h3 inline-block shrink-0">{title}</span>
                    {subtitle.length > 0 && (
                      <span className="mt-1 block">{subtitle}</span>
                    )}
                  </div>
                </div>
                {hasFilters && <hr className="mt-1" />}
              </CardBody>
            </Card>
          )}
          {hasFilters && (
            <>
              {!useNewActionbar && (
                <Card
                  padding="dense"
                  raised={false}
                  bgColor="default"
                  className={`mb-4 mt-4 border duration-200 transition-opacity${
                    loading ? ' pointer-events-none opacity-50' : ''
                  }`}
                >
                  <CardBody>
                    <div className="inline-flex w-full items-center justify-between">
                      <span
                        className="h5 mr-4 shrink-0"
                        {...(filtersTitleTestId
                          ? {
                              id: filtersTitleTestId,
                              name: filtersTitleTestId,
                              'data-testid': filtersTitleTestId
                            }
                          : {})}
                      >
                        Filters
                      </span>
                      <div className="flex-auto">
                        {hasActiveFilters && (
                          <div className="flex flex-auto flex-wrap items-center">
                            {Array.from(activeFiltersForUi).map(
                              ([key, filterState]) => (
                                <span
                                  key={key}
                                  className="my-0.5 mr-2 inline-flex items-center rounded-lg bg-primary-100 px-1 py-0.5 text-primary"
                                >
                                  <span className="inline-flex space-x-1 px-1 py-0.5 text-left text-xs leading-none">
                                    <span className="font-medium">
                                      {filterState.item?.label}:
                                    </span>
                                    <span className="font-semibold">
                                      {Array.isArray(filterState.label)
                                        ? `"${filterState.label.join('→')}"`
                                        : `"${filterState.label}"`}
                                    </span>
                                  </span>
                                  <IconButton
                                    size="sm"
                                    mode="ghost"
                                    testIdentifier={`${dataTableTestPrefix}-filter-${key.replace(
                                      /[_.]/g,
                                      '-'
                                    )}-remove`}
                                    onClick={() =>
                                      filterState.item &&
                                      handleRemoveFilter(
                                        filterState.item?.filterKey
                                      )
                                    }
                                  >
                                    <XIcon size={16} className="text-primary" />
                                  </IconButton>
                                </span>
                              )
                            )}
                          </div>
                        )}
                      </div>
                      {/* Desktop view */}
                      <div className="mr-6 hidden shrink-0 items-center gap-4 md:flex">
                        <Button
                          size="sm"
                          variant="secondary"
                          leftSlot={
                            hasActiveFilters ? <ArrowLeftRightIcon /> : <FilterIcon />
                          }
                          onClick={() => drawerCtx.open(filterDrawerName)}
                          testIdentifier={`${dataTableTestPrefix}-filters-open`}
                        >
                          {hasActiveFilters
                            ? 'Change filters'
                            : 'Select filters'}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          leftSlot={<RotateCcwIcon />}
                          disabled={activeFiltersForUi.size === 0}
                          onClick={handleFilterReset}
                          testIdentifier={`${dataTableTestPrefix}-filters-reset`}
                        >
                          Reset filters
                        </Button>
                      </div>
                      {/* Mobile view */}
                      <div
                        ref={dropdownWrapperRef}
                        className="relative mr-6 flex shrink-0 items-center md:hidden"
                      >
                        <div ref={dropdownButtonRef}>
                          <IconButton
                            icon={<EllipsisVerticalIcon />}
                            mode={IconButtonModes.Ghost}
                            variant={IconButtonVariants.Tertiary}
                            size={ButtonSizes.SM}
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            testIdentifier={`${dataTableTestPrefix}-filters-mobile-menu`}
                          />
                        </div>
                        <div ref={dropdownRef}>
                          <Dropdown
                            open={dropdownOpen}
                            testIdentifier={`${dataTableTestPrefix}-filters-mobile-menu-dropdown`}
                            options={[
                              {
                                label: hasActiveFilters
                                  ? 'Change filters'
                                  : 'Select filters',
                                value: 'select-filters',
                                icon: hasActiveFilters ? (
                                  <ArrowLeftRightIcon />
                                ) : (
                                  <FilterIcon />
                                ),
                                href: undefined
                              },
                              {
                                label: 'Reset filters',
                                value: 'reset-filters',
                                icon: <RotateCcwIcon />,
                                href: undefined
                              }
                            ]}
                            handleSelectOption={(value) => {
                              setDropdownOpen(false);
                              if (value === 'select-filters') {
                                drawerCtx.open(filterDrawerName);
                              } else if (value === 'reset-filters') {
                                handleFilterReset();
                              }
                            }}
                            position="left"
                          />
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}
              {/* Drawer for filters */}
              <Drawer
                name={filterDrawerName}
                testIdentifier={`${dataTableTestPrefix}-filters-drawer`}
                onClose={() => {
                  setFiltersState(activeFilters);
                }}
              >
                <DrawerStage
                  title={filterDrawerTitle || 'Filters'}
                  leftSlot={<FilterIcon size={32} className="text-gray-500" />}
                  className="min-h-0 flex-1"
                  handleClose={() => {
                    setFiltersState(activeFilters);
                    drawerCtx.close(filterDrawerName);
                  }}
                  testIdentifier={`${dataTableTestPrefix}-filters-drawer-stage`}
                  actions={
                    <div className="flex w-full items-center justify-between">
                      {showDrawerResetFilters ? (
                        <Button
                          variant="tertiary"
                          mode="ghost"
                          leftSlot={<RotateCcwIcon />}
                          onClick={handleFilterReset}
                          testIdentifier={`${dataTableTestPrefix}-filters-drawer-reset`}
                        >
                          Reset filters
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          leftSlot={<XCircleIcon />}
                          onClick={() => drawerCtx.close(filterDrawerName)}
                          testIdentifier={`${dataTableTestPrefix}-filters-drawer-cancel`}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        onClick={async () => {
                          if (savedFilterSection && savedFiltersRef.current) {
                            const ok =
                              await savedFiltersRef.current.persistIfNeeded();
                            if (!ok) return;
                          }
                          handleApplyFilter();
                        }}
                        testIdentifier={`${dataTableTestPrefix}-filters-drawer-apply`}
                      >
                        Apply
                      </Button>
                    </div>
                  }
                >
                  {filtersList && (
                    <div key={appliedFiltersFingerprint(activeFilters)}>
                      {showAppliedSortInFilterDrawer && (
                        <AppliedSortTags
                          tags={appliedSortTags}
                          onRemove={handleClearAppliedSort}
                          testIdentifierPrefix={dataTableTestPrefix}
                        />
                      )}
                      {savedFilterSection ? (
                        <SavedFiltersDrawerSection
                          ref={savedFiltersRef}
                          section={savedFilterSection}
                          contextId={savedFilterContextId}
                          filtersList={filtersList}
                          filtersState={filtersState}
                          onHydrateFilters={setFiltersState}
                          onReset={handleFilterReset}
                          testIdentifierPrefix={`${dataTableTestPrefix}-saved-filters`}
                        >
                          {renderFilterDrawerGrid({
                            filtersList,
                            filtersState,
                            handleFilterInputChange,
                            handleFilterSelectChange,
                            handleAutoCompleteChange,
                            getFilterTestIdentifierFn: getFilterTestIdentifier
                          })}
                        </SavedFiltersDrawerSection>
                      ) : (
                        renderFilterDrawerGrid({
                          filtersList,
                          filtersState,
                          handleFilterInputChange,
                          handleFilterSelectChange,
                          handleAutoCompleteChange,
                          getFilterTestIdentifierFn: getFilterTestIdentifier
                        })
                      )}
                    </div>
                  )}
                </DrawerStage>
              </Drawer>
            </>
          )}
        </div>
      )}
      {showError ? (
        <div role="alert" className="DataTable__LoadError">
          <InlineNotification
            mode={InlineNotificationModes.Error}
            heading="Couldn't load results"
            message={
              isFetching ? 'Retrying…' : 'Check your connection and try again.'
            }
            actions={
              isFetching
                ? undefined
                : [{ label: 'Retry', onClick: handleRetry }]
            }
            testIdentifier={`${dataTableTestPrefix}-load-error`}
          />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          count={total}
          itemsToArchiveCount={itemsToArchiveCount}
          bulkErrorText={bulkErrorText}
          showBulkError={showBulkError}
          handleBulkErrorClick={handleBulkErrorClick}
          bulkErrorDismissTestId={resolvedBulkErrorDismissTestId}
          page={page}
          onPageChange={(page) => handleChangePage(page)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(perPage) => handleChangePerPage(perPage)}
          paginationProps={paginationProps}
          loading={loading}
          actions={actions}
          keyField={keyField}
          uniqueKeyField={uniqueKeyField}
          selected={selected}
          onSelect={bulkActions ? setSelected : undefined}
          disableSelectFor={
            disabledBulkIds.length > 0 ? disabledBulkIds : undefined
          }
          sort={sort}
          onSortChange={(sortedEntity) =>
            handleSortChange(sortedEntity as DataTableSortData)
          }
          onRowClicked={onRowClicked}
          onCellClick={onCellClick}
          emptyState={hasActiveFilters ? undefined : emptyState}
          hasActiveFilters={
            hasActiveFilters || (!!searchTerm && searchTerm !== '')
          }
          noResultsText={noResultsText}
          appendAfterRow={appendAfterRow}
          isResponsive={isResponsive}
          selectAllHeaderTestId={resolvedSelectAllHeaderTestId}
          rowSelectTestIdentifier={resolveRowSelectTestIdentifier}
          actionsHeaderTestId={resolvedActionsHeaderTestId}
          showPagination={showPagination}
          isFixedLayout={isFixedLayout}
          refreshing={isRefreshing}
        />
      )}
      {column2 && items.length > 0 && (
        <DataTable
          columns={column2}
          data={items}
          count={total}
          itemsToArchiveCount={itemsToArchiveCount}
          bulkErrorText={bulkErrorText}
          showBulkError={showBulkError}
          handleBulkErrorClick={handleBulkErrorClick}
          bulkErrorDismissTestId={resolvedBulkErrorDismissTestId}
          page={page}
          onPageChange={(page) => handleChangePage(page)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(perPage) => handleChangePerPage(perPage)}
          paginationProps={paginationProps}
          loading={loading}
          actions={actions}
          keyField={keyField}
          uniqueKeyField={uniqueKeyField}
          selected={selected}
          onSelect={bulkActions ? setSelected : undefined}
          disableSelectFor={
            disabledBulkIds.length > 0 ? disabledBulkIds : undefined
          }
          sort={sort}
          onSortChange={(sortedEntity) =>
            handleSortChange(sortedEntity as DataTableSortData)
          }
          onRowClicked={onRowClicked}
          emptyState={hasActiveFilters ? undefined : emptyState}
          hasActiveFilters={
            hasActiveFilters || (!!searchTerm && searchTerm !== '')
          }
          noResultsText={noResultsText}
          appendAfterRow={appendAfterRow}
          isResponsive={isResponsive}
          selectAllHeaderTestId={`${dataTableTestPrefix}-select-all-secondary`}
          rowSelectTestIdentifier={resolveRowSelectTestIdentifier}
          actionsHeaderTestId={resolvedActionsHeaderTestId}
          showPagination={showPagination}
          isFixedLayout={isFixedLayout}
          refreshing={isRefreshing}
        />
      )}
    </>
  );
}
